import { useTasks } from 'hooks';
import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import WidgetSectionTitle from 'widgets/WidgetSectionTitle'
import { Table, Tag, Spin, } from "antd";
import CounterOfferSummary from './CounterOfferSummary';

export default function TaskCounterOffers() {
    const navigate = useNavigate();
    const { taskId } = useParams();

    const { getTaskById } = useTasks();

    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const data = await getTaskById(taskId);
                setTask(data);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [taskId, getTaskById]);

    const counterOffers = useMemo(() => {
        return task?.counterOffers || [];
    }, [task]);

    const columns = [
    {
      title: "Helper",
      dataIndex: "helper",
      key: "helper",
      render: (helper) => (
        <div>
          <strong>
            {helper?.firstName} {helper?.lastName}
          </strong>
          <div style={{ fontSize: 12, color: "#888" }}>
            {helper?.email}
          </div>
        </div>
      ),
    },

    {
      title: "Offer",
      key: "offer",
      render: (_, record) => (
        <div>
          <div>
            💰 ${record.amount} | ⏱ {record.durationMinutes} min
          </div>
          <div style={{ fontSize: 12, color: "#888" }}>
            {record.reason}
          </div>
        </div>
      ),
    },

    {
      title: "Response",
      key: "response",
      render: (_, record) =>
        record.responseStatus ? (
          <div>
            <div>
              💰 ${record.responseAmount} | ⏱ {record.responseDurationMinutes} min
            </div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {record.responseReason}
            </div>
          </div>
        ) : (
          <Tag>Pending</Tag>
        ),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const color =
          status === "ACCEPTED"
            ? "green"
            : status === "DECLINED"
            ? "red"
            : status === "FINAL_OFFER"
            ? "blue"
            : "orange";

        return <Tag color={color}>{status}</Tag>;
      },
    },

    {
      title: "Interested",
      dataIndex: "isStillInterested",
      key: "isStillInterested",
      render: (val) =>
        val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
    },

    {
        title: "Outcome",
        key: "outcome",
        render: (_, record) => {
            if (record.responseStatus === "ACCEPTED") return <Tag color="green">Accepted</Tag>;
            if (record.responseStatus === "DECLINED") return <Tag color="red">Declined</Tag>;
            return <Tag color="orange">In Progress</Tag>;
        },
    }
  ];


    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
                <Spin />
            </div>
        );
    }

  return (
    <>
        <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3">
            <WidgetSectionTitle
                icon="exchange-alt"
                title="Task Counter offers"
                subtitle="Review and manage all counter offers related to this task"
                transform="shrink-2"
                className="flex-1"
            />

            <Button
                variant="secondary"
                size="sm"
                className="text-sm me-2"
                onClick={() => navigate(-1)}
            >
                Go Back
            </Button>
        </div>

        <CounterOfferSummary counterOffers={counterOffers} />

        <Table
            dataSource={counterOffers}
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            expandable={{
        expandRowByClick: true,
        expandIcon: ({ expanded, onExpand, record }) => (
          <button
            type="button"
            onClick={(event) => onExpand(record, event)}
            aria-label={expanded ? "Collapse details" : "Expand details"}
            title={expanded ? "Collapse details" : "Expand details"}
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              border: "1px solid #d9e2ec",
              background: expanded ? "#e8f5e9" : "#fff",
              color: expanded ? "#2e7d32" : "#475569",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {expanded ? "[]" : "[]"}
          </button>
        ),
        expandedRowRender: (record) => (
          <div style={{ padding: 12, background: "#fafafa", borderRadius: 6 }}>
            <p className="mb-2">
              <strong>Submitted At:</strong>{" "}
              {record.submittedAt
                ? new Date(record.submittedAt).toLocaleString()
                : "N/A"}
            </p>

            <p className="mb-2">
              <strong>Response Status:</strong>{" "}
              {record.responseStatus ? (
                <Tag color="blue">{record.responseStatus}</Tag>
              ) : (
                <Tag>Not Responded</Tag>
              )}
            </p>

            <p className="mb-2">
              <strong>Response Amount:</strong>{" "}
              {record.responseAmount ?? "—"}
            </p>

            <p className="mb-2">
              <strong>Response Duration:</strong>{" "}
              {record.responseDurationMinutes ?? "—"} min
            </p>

            <p className="mb-2">
              <strong>Response Reason:</strong>{" "}
              {record.responseReason || "—"}
            </p>

            <p className="mb-2">
              <strong>Response At:</strong>{" "}
              {record.responseAt
                ? new Date(record.responseAt).toLocaleString()
                : "—"}
            </p>

            <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #eee" }}>
              <p className="mb-1">
                <strong>Helper Interest:</strong>{" "}
                {record.isStillInterested ? (
                  <Tag color="green">Still Interested</Tag>
                ) : (
                  <Tag color="red">Withdrawn</Tag>
                )}
              </p>
            </div>
          </div>
        ),

        rowExpandable: (record) =>
          !!(
            record.reason ||
            record.responseReason ||
            record.responseStatus ||
            record.submittedAt
          ),
      }}
        />
    </>
  )
}
